import type { SessionUser } from '@hm/contracts'
import {
  BarChart3,
  Boxes,
  Building2,
  ClipboardList,
  Handshake,
  History,
  LayoutGrid,
  MessagesSquare,
  Package,
  PackageCheck,
  Receipt,
  Search,
  Settings,
  ShoppingCart,
  Store,
  Tags,
  Truck,
  Users,
  Wallet,
} from 'lucide-react'
import type { ComponentType } from 'react'
import type { MessageKey } from '~/i18n'
import { can, isBuyer, isSeller } from '~/lib/access'

/**
 * Who sees which screen.
 *
 * Kept apart from the shell because it is a rule rather than markup, and rules are
 * worth testing on their own — this one shipped wrong once: counterparty screens
 * are gated on the kind of account, never on a permission (see `lib/access.ts`).
 */

export type NavGroup = 'work' | 'catalog' | 'admin'

export interface NavItem {
  to: string
  label: MessageKey
  icon: ComponentType<{ className?: string }>
  show: (user: SessionUser) => boolean
  /** Only these appear in the phone tab bar; the rest live in the sidebar. */
  primary?: true
  /**
   * Hidden until the user turns Advanced mode on. Hiding, not forbidding — the
   * route still answers if somebody types the address or follows an old link,
   * because a preference is not a permission.
   */
  advanced?: true
  group: NavGroup
}

/**
 * Labels are the words a shop owner uses, not the words the database uses —
 * "Price requests" rather than "RFQs", "Buy more, pay less" rather than "tiers".
 *
 * One list serves three very different roles. A buyer never learns the supply-side
 * screens exist; a shop never sees the demand side. It is the same matrix the
 * server enforces, so the nav cannot offer a dead end.
 */
export const NAV: NavItem[] = [
  { to: '/', label: 'nav.home', icon: LayoutGrid, show: () => true, primary: true, group: 'work' },

  // Customer accounts
  { to: '/shop', label: 'common.shop', icon: Store, show: isBuyer, primary: true, group: 'work' },
  {
    to: '/cart',
    label: 'nav.cart',
    icon: ShoppingCart,
    show: isBuyer,
    primary: true,
    group: 'work',
  },

  // Shop accounts
  {
    to: '/items',
    label: 'common.my_items',
    icon: Package,
    show: isSeller,
    primary: true,
    group: 'work',
  },
  {
    to: '/price-checks',
    label: 'common.price_checks',
    icon: ClipboardList,
    show: isSeller,
    primary: true,
    group: 'work',
  },
  {
    to: '/supply-orders',
    label: 'common.orders_to_fill',
    icon: PackageCheck,
    show: isSeller,
    primary: true,
    group: 'work',
  },

  // Shared deal screens
  {
    to: '/requests',
    label: 'common.price_requests',
    icon: ClipboardList,
    show: (u) => can('rfq:manage')(u) || can('rfq:read_own')(u),
    primary: true,
    group: 'work',
  },
  {
    to: '/orders',
    label: 'common.orders',
    icon: Receipt,
    show: (u) => can('order:manage')(u) || can('order:read_own')(u),
    primary: true,
    group: 'work',
  },
  {
    to: '/messages',
    label: 'common.messages',
    icon: MessagesSquare,
    show: can('chat:participate'),
    primary: true,
    group: 'work',
  },

  // The desk's catalog
  {
    to: '/catalog',
    label: 'common.item_list',
    icon: Tags,
    show: can('product:create'),
    group: 'catalog',
  },
  {
    to: '/pricing',
    label: 'common.customer_prices',
    icon: Tags,
    show: can('admin_listing:read'),
    group: 'catalog',
  },
  {
    to: '/stock',
    label: 'common.our_stock',
    icon: Boxes,
    show: can('stock:read_any'),
    group: 'catalog',
  },
  {
    to: '/sourcing',
    label: 'common.shop_prices',
    icon: Search,
    show: can('sourcing:manage'),
    group: 'catalog',
  },
  {
    to: '/purchase-orders',
    label: 'common.shop_orders',
    icon: Truck,
    show: can('purchase_order:manage'),
    group: 'catalog',
  },
  {
    to: '/price-books',
    label: 'nav.price_books',
    icon: Handshake,
    show: can('price_book:manage'),
    advanced: true,
    group: 'catalog',
  },

  // Oversight
  {
    to: '/businesses',
    label: 'common.businesses',
    icon: Building2,
    show: can('org:read'),
    group: 'admin',
  },
  { to: '/people', label: 'common.people', icon: Users, show: can('user:read'), group: 'admin' },
  { to: '/money', label: 'common.money', icon: Wallet, show: can('ledger:read'), group: 'admin' },
  {
    to: '/reports',
    label: 'common.numbers',
    icon: BarChart3,
    show: can('report:read'),
    group: 'admin',
  },
  {
    to: '/activity',
    label: 'common.activity',
    icon: History,
    show: can('audit:read'),
    group: 'admin',
  },
  { to: '/settings', label: 'common.settings', icon: Settings, show: () => true, group: 'admin' },
]

export const GROUP_LABELS: Record<NavGroup, MessageKey> = {
  work: 'nav.group.work',
  catalog: 'common.catalog',
  admin: 'common.office',
}

export const NAV_GROUPS: NavGroup[] = ['work', 'catalog', 'admin']

/**
 * Two filters, and they are not the same kind of thing.
 *
 * `show` is the permission matrix — a screen it rejects would refuse the user
 * anyway, so offering it would be a dead end. `advanced` is a preference: the
 * screen works perfectly well, it is just not what anybody needs on a normal day,
 * and a sidebar of twenty entries is how a simple job starts to feel hard.
 */
export function navFor(user: SessionUser): NavItem[] {
  return NAV.filter((item) => item.show(user) && (user.advancedMode || !item.advanced))
}

/**
 * Thumb-reachable navigation, capped at five. A sixth tab makes every tab too
 * narrow to hit, so anything past the cap lives in the sidebar and on Home.
 */
export const MAX_TABS = 5

export function tabsFor(items: NavItem[]): NavItem[] {
  return items.filter((i) => i.primary).slice(0, MAX_TABS)
}
